import React from "react";
import { Plural, Trans } from "@lingui/macro";
import {
  DescriptionTextColumnContent,
  DescriptionTextColumnContentLeft,
  DescriptionTextColumnContentRight,
  DescriptionTextColumnTitle,
  SectionContainer,
  SectionTitleHeading
} from "@silversea-ssc/ui-components-react";
import { SectionTitle } from "@silversea-ssc/ui-components-react";
import { CruiseSectionShipFragment } from "types/graphql-types";
import { DescriptionTextColumn } from "@silversea-ssc/ui-components-react";
import { Button } from "@silversea-ssc/ui-components-react";
import { ResponsiveSlideshow } from "components/Slideshow/Slideshow";
import ServiceRows from "components/ServiceRow/ServiceRows";
import ServiceRow, {
  ServiceRowContent,
  ServiceRowImages,
  ServiceRowTitle
} from "components/ServiceRow/ServiceRow";
import Slider, { SliderSlide } from "components/Slider";
import AsCardSlide from "components/Card/AsCardSlide";
import * as styles from "./Cruise.module.scss";
import TextBox from "components/TextBox/TextBox";
import { ButtonsContainer } from "@silversea-ssc/ui-components-react";
import { isPresent } from "src/utils/checks";

export interface CruiseSectionShipProps {
  ship: CruiseSectionShipFragment;
}

const CruiseSectionShip: React.FC<CruiseSectionShipProps> = ({ ship }) => {
  const diningAreas = ship.diningAreas?.filter(isPresent);
  const facilities = ship.facilities?.filter(isPresent);
  const slideshow = ship.slideshow?.filter(isPresent);

  return (
    <React.Fragment>
      <SectionContainer
        spacingTop="regular"
        spacingBottom="regular"
        backgroundColor="white"
        dataContentName="section_ship"
      >
        <DescriptionTextColumn>
          <DescriptionTextColumnContent>
            <DescriptionTextColumnContentLeft>
              <DescriptionTextColumnTitle>
                <Trans>Ship</Trans> - {ship.name}
              </DescriptionTextColumnTitle>

              <TextBox
                fontSize="medium"
                content={ship.description?.content?.localized}
              />
              <div className={styles.discoverMoreShip}>
                {ship.fullPath && (
                  <React.Fragment>
                    <Trans>Ship Details</Trans>:{" "}
                    <a href={`https://www.silversea.com${ship.fullPath}`}>
                      https://www.silversea.com{ship.fullPath}
                    </a>
                  </React.Fragment>
                )}
              </div>
              {ship.deckPlan && (
                <ButtonsContainer spacingTop="regular" align="left">
                  <Button
                    href={`${ship.deckPlan?.asset?.url}?dl=`}
                    variant="primary"
                    iconName="download"
                    target="_blank"
                    data-content-piece="download_deck_plan"
                    data-content-params={{ shipName: ship.name }}
                  >
                    <Trans>Download Deck Plan</Trans>
                  </Button>
                </ButtonsContainer>
              )}
            </DescriptionTextColumnContentLeft>
            <DescriptionTextColumnContentRight>
              {slideshow && slideshow.length > 0 && (
                <ResponsiveSlideshow items={slideshow} variant="gray" />
              )}
            </DescriptionTextColumnContentRight>
          </DescriptionTextColumnContent>
        </DescriptionTextColumn>
      </SectionContainer>
      <div
        className={styles.diningOptions}
        data-content-name="section_restaurants"
      >
        <SectionContainer
          spacingTop="regular"
          spacingBottom="regular"
          backgroundColor="destinationExtralight"
        >
          <SectionTitle variant="wide">
            <SectionTitleHeading>
              {ship.name} <Trans>Dining Options</Trans>:<br />{" "}
              <Plural
                value={diningAreas?.length || 0}
                one="# Restaurant"
                other="# Restaurants"
              />
            </SectionTitleHeading>
          </SectionTitle>

          {diningAreas && (
            <ServiceRows
              readMore={diningAreas.length > 2}
              openReadMoreLabel={<Trans>View all dining options</Trans>}
              closeReadMoreLabel={<Trans>View less</Trans>}
            >
              {diningAreas.map((service, index) => {
                return (
                  <React.Fragment key={service._key}>
                    {service.card && (
                      <ServiceRow
                        key={service._key}
                        variant={index % 2 ? "right" : "left"}
                        backgroundColor="white"
                      >
                        <ServiceRowImages items={[service.card.image]} />
                        <ServiceRowTitle>
                          {service.card.title?.localized}
                        </ServiceRowTitle>
                        <ServiceRowContent>
                          {service.card.description?.localized}
                        </ServiceRowContent>
                      </ServiceRow>
                    )}
                  </React.Fragment>
                );
              })}
            </ServiceRows>
          )}
        </SectionContainer>
      </div>
      {facilities && facilities.length > 0 && (
        <div
          className={styles.publicAreas}
          data-content-name="section_public_areas"
        >
          <SectionContainer
            spacingTop="regular"
            spacingBottom="regular"
            backgroundColor="white"
          >
            <SectionTitle variant="wide">
              <SectionTitleHeading>
                <Trans>Public Areas</Trans>
              </SectionTitleHeading>
            </SectionTitle>
            <Slider variantColor="neutral05">
              {facilities.map((facility) => {
                return (
                  <SliderSlide key={facility._key}>
                    {facility.card && <AsCardSlide card={facility.card} />}
                  </SliderSlide>
                );
              })}
            </Slider>
          </SectionContainer>
        </div>
      )}
    </React.Fragment>
  );
};

export default React.memo(CruiseSectionShip);
